import React, { memo, useCallback, useLayoutEffect, useMemo } from 'react';
import { View, StyleSheet } from 'react-native';
import Button from 'design-system/Button';
import { useDisableAndroidBackButton } from 'hooks';
import SuccessView from 'design-system/SuccessView';
import WeLearnFromYouCard from 'design-system/WeLearnFromYouCard';
import { Spacer } from 'design-system';
import EmptyComponent from 'design-system/EmptyComponent';
import { goBackWithKey } from 'utils/navigationv6';
import useColors from 'hooks/useColors';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import StatusBar from 'design-system/StatusBar';
import { isWeb } from '../../../constants';
const WE_LEARN_FROM_YOU_EDIT_TYPES = [
    'categorise',
    'merchantLogo',
    'tags',
];
const EditCompleteScreen = memo(({ navigation, route }) => {
    const colors = useColors();
    const { editData, keyBack, transactionsCount } = route.params;
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    useLayoutEffect(() => {
        navigation.setOptions({
            gestureEnabled: false,
            headerLeft: EmptyComponent,
            headerTintColor: colors.elements.primary,
        });
    }, [colors.elements.primary]);
    const goBack = useCallback(() => {
        if (keyBack?.startsWith('TransactionsList') ||
            keyBack?.startsWith('SearchTransactions')) {
            navigation.pop();
        }
        else {
            goBackWithKey(navigation, keyBack);
        }
    }, [keyBack]);
    useDisableAndroidBackButton(() => {
        goBack();
        return true;
    });
    const title = useMemo(() => {
        const { editData } = route.params;
        if (editData.editType === 'tags') {
            if (!editData.tags.length) {
                return `${transactionsCount} ${transactionsCount > 1 ? 'Transactions' : 'Transaction'} tags removed`;
            }
            return `${transactionsCount === 1
                ? 'Transaction is'
                : `${transactionsCount} Transactions are`} tagged`;
        }
        return `${transactionsCount} ${transactionsCount > 1 ? 'Transactions' : 'Transaction'}`;
    }, [route.params, transactionsCount]);
    const text = useMemo(() => {
        switch (editData.editType) {
            case 'rename': {
                const { customName } = editData;
                return `${transactionsCount > 1 ? 'are now' : 'has been'} renamed as ${customName}.`;
            }
            case 'categorise': {
                const { category } = editData;
                return `${transactionsCount > 1 ? 'are' : 'is'} now categorised as ${category?.displayName}.`;
            }
            case 'merchantLogo':
                if (editData.isDelete) {
                    return transactionsCount > 1
                        ? 'now have their original logos'
                        : 'now has its original logo';
                }
                return transactionsCount > 1
                    ? 'now have updated logos'
                    : 'now has an updated logo';
            case 'tags': {
                const { tags } = editData;
                if (!tags.length)
                    return '';
                return `as ${tags.map((tag) => `#${tag}`).join(', ')}`;
            }
            case 'note':
                return `${transactionsCount > 1 ? 'have' : 'has'} a note now!`;
            case 'date':
                return `date ${transactionsCount > 1 ? 'have' : 'has'} changed!`;
            case 'group':
                return `${transactionsCount > 1 ? 'are' : 'is'} added to the ${editData.groupName} group!`;
            default:
                return '';
        }
    }, [editData, transactionsCount]);
    return (<View style={styles.container}>
        <StatusBar barStyle="dark-content" animated/>
        <SuccessView title={title} subTitle={text}>
          <View style={paddingHorizontalStyle}>
            {WE_LEARN_FROM_YOU_EDIT_TYPES.includes(editData.editType) && (<>
                <WeLearnFromYouCard />
                <Spacer.H24 />
              </>)}
            <Button brand title="Perfect, let’s go back!" onPress={goBack} testID="backButton"/>
          </View>
        </SuccessView>
      </View>);
});
const styles = StyleSheet.create({
    container: {
        flex: 1,
    },
});
export default EditCompleteScreen;
